import type { BadgeProps, BadgeVariant } from '@meterup/atto';
import type { Key } from 'react';
import {
  Badge,
  BadgeContainer,
  ComboBox,
  ComboBoxItem,
  ComboBoxSection,
  HStack,
  Label,
  SelectTrigger,
  sizing,
  styled,
  ToggleInput,
  useCurrentCrumbs,
  useViewport,
} from '@meterup/atto';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { getActiveControllerForNetwork } from '../hooks/useActiveControllerForNetwork';
import { useNetworkSlugFromPathOrNull } from '../hooks/useNetworkFromPath';
import { type Network, useNetworksForCompany } from '../hooks/useNetworksForCompany';
import { useCurrentCompany } from '../providers/CurrentCompanyProvider';
import { getNextBestCurrentPathForNetwork } from '../utils/bestPath';
import { makeLink } from '../utils/main_and_drawer_navigation';
import { getNetworkAddress } from '../utils/network';

const LocationSwitcherOptionContainer = styled(HStack, {
  width: '100%',
});

const LocationSwitcherLabelContainer = styled('span', {
  flex: '1 1 auto',
});

const LocationSwitcherContainer = styled('div', {
  gridArea: 'sites',
  display: 'flex',
  width: '100%',
  minWidth: 0,

  [`& ${SelectTrigger} ${LocationSwitcherOptionContainer} ${BadgeContainer}`]: {
    display: 'none',
  },

  variants: {
    view: {
      pane: {
        padding: `${sizing.contentEnds} ${sizing.sides}`,
      },
      sidebar: {
        padding: `$10 ${sizing.sides}`,
      },
      tools: {},
    },
  },
});

type LocationSwitcherNetwork = Pick<
  Network,
  'label' | 'isActive' | 'isCustomer' | 'isTrial' | 'isTemplate' | 'deactivatedAt' | 'goLiveAt'
>;

export enum NetworkState {
  Template,
  PreLive,
  Trial,
  Active,
  Inactive,
  Deactivated,
}

// eslint-disable-next-line consistent-return
export function networkStateSortOrder(state: NetworkState): number {
  switch (state) {
    case NetworkState.Template:
      return 1;
    case NetworkState.PreLive:
      return 2;
    case NetworkState.Active:
    case NetworkState.Trial:
      return 3;
    case NetworkState.Inactive:
    case NetworkState.Deactivated:
      return 4;
  }
}

export function getNetworkState(
  network: Pick<
    LocationSwitcherNetwork,
    'isTemplate' | 'isTrial' | 'isActive' | 'isCustomer' | 'goLiveAt' | 'deactivatedAt'
  >,
): NetworkState {
  if (network.isTemplate) return NetworkState.Template;

  if (network.isTrial) return NetworkState.Trial;

  if (network.isActive) return NetworkState.Active;

  const now = DateTime.now();

  if (network.isCustomer) {
    if (network.goLiveAt) {
      const goLiveAt = DateTime.fromISO(network.goLiveAt);
      if (goLiveAt.valueOf() > now.valueOf()) {
        return NetworkState.PreLive;
      }
    }
    if (network.deactivatedAt) {
      const deactivatedAt = DateTime.fromISO(network.deactivatedAt);
      if (deactivatedAt.valueOf() <= now.valueOf()) {
        return NetworkState.Deactivated;
      }
    }
  }
  return NetworkState.Inactive;
}

export function isActiveLike(networkState: NetworkState): boolean {
  switch (networkState) {
    case NetworkState.Template:
    case NetworkState.Deactivated:
    case NetworkState.Inactive:
      return false;
    case NetworkState.Trial:
    case NetworkState.PreLive:
    case NetworkState.Active:
    default:
      return true;
  }
}

export function isOnboardingLike(networkState: NetworkState): boolean {
  switch (networkState) {
    case NetworkState.Template:
    case NetworkState.Deactivated:
    case NetworkState.Active:
      return false;
    case NetworkState.Inactive:
    case NetworkState.Trial:
    case NetworkState.PreLive:
    default:
      return true;
  }
}

// eslint-disable-next-line consistent-return
export function networkStateLabel(networkState: NetworkState): string {
  switch (networkState) {
    case NetworkState.Template:
      return 'Template';
    case NetworkState.Deactivated:
      return 'Deactivated';
    case NetworkState.Active:
      return 'Active';
    case NetworkState.Inactive:
      return 'Inactive';
    case NetworkState.Trial:
      return 'Trial';
    case NetworkState.PreLive:
      return 'Pre-live';
  }
}

// eslint-disable-next-line consistent-return
export function networkStateBadgeVariant(state: NetworkState): BadgeVariant {
  switch (state) {
    case NetworkState.Template:
      return 'alternative';
    case NetworkState.Trial:
    case NetworkState.Active:
      return 'brand';
    case NetworkState.PreLive:
    case NetworkState.Deactivated:
    case NetworkState.Inactive:
      return 'neutral';
  }
}

export function NetworkStateBadge({
  state,
  size = 'small',
  ...rest
}: { state: NetworkState } & Partial<BadgeProps>) {
  if (state === NetworkState.Active) return null;

  return (
    <Badge size={size} variant={networkStateBadgeVariant(state)} elide={false} {...rest}>
      {networkStateLabel(state)}
    </Badge>
  );
}

export function LocationSwitcherOption({
  network,
  label,
}: {
  network: LocationSwitcherNetwork;
  label?: string;
}) {
  const displayLabel = label || network.label;

  return (
    <LocationSwitcherOptionContainer spacing={4} justify="between">
      <LocationSwitcherLabelContainer>{displayLabel}</LocationSwitcherLabelContainer>

      <NetworkStateBadge state={getNetworkState(network)} size="x-small" />
    </LocationSwitcherOptionContainer>
  );
}

const LocationSwitcherInactiveToggle = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$6',
});

const LocationSwitcherActions = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
});

export function LocationSwitcher({
  view,
  networkSlug: propNetworkSlug,
  shouldNotChangePages,
  routePathsNotToChange,
  inOnboarding = false,
}: {
  view?: 'pane' | 'sidebar' | 'tools';
  routePathsNotToChange?: string[];
  networkSlug?: string;
  shouldNotChangePages?: boolean;
  inOnboarding?: boolean;
}) {
  const { breakpoint } = useViewport();
  const companyName = useCurrentCompany();
  const navigate = useNavigate();

  const [showNonLiveNetworks, setShowNonLiveNetworks] = useState(false);

  const networkSlugFromPath = useNetworkSlugFromPathOrNull();
  const networks = useNetworksForCompany(companyName);
  const location = useLocation();

  const networkSlug = useMemo<string | undefined>(() => {
    if (propNetworkSlug) return propNetworkSlug;

    if (networkSlugFromPath) return networkSlugFromPath;

    return undefined;
  }, [propNetworkSlug, networkSlugFromPath]);

  const ref = useRef(null);

  const selectedNetwork = useMemo(
    () => networks.find((network) => network.slug === networkSlug),
    [networks, networkSlug],
  );

  useEffect(() => {
    if (!selectedNetwork) return;

    if (!isActiveLike(getNetworkState(selectedNetwork))) {
      setShowNonLiveNetworks(true);
    }
  }, [selectedNetwork]);

  const networksToShow = useMemo(() => {
    let filteredNetworks = networks;
    if (!showNonLiveNetworks) {
      if (inOnboarding) {
        filteredNetworks = filteredNetworks.filter(
          (network) => isOnboardingLike(getNetworkState(network)) || networkSlug === network.slug,
        );
      } else {
        filteredNetworks = filteredNetworks.filter(
          (network) => isActiveLike(getNetworkState(network)) || networkSlug === network.slug,
        );
      }
    }
    if (filteredNetworks.length < 0) {
      filteredNetworks = networks;
    }

    return filteredNetworks;
  }, [networks, showNonLiveNetworks, inOnboarding, networkSlug]);

  const { crumbs } = useCurrentCrumbs();

  const valueSelected = useCallback(
    (selectedNetworkSlug: Key) => {
      if (typeof selectedNetworkSlug !== 'string')
        throw new Error('Unexpected key for selectedNetworkSlug');

      const network = networks.find((n) => n.slug === selectedNetworkSlug);
      const controllerName = getActiveControllerForNetwork(network ?? null)?.hardwareDevice
        ?.serialNumber;

      if (shouldNotChangePages && routePathsNotToChange?.length) {
        const pageMatch = routePathsNotToChange
          ?.map((path) =>
            matchPath(
              {
                path,
                end: false,
              },
              location.pathname,
            ),
          )
          .filter((match) => match);
        const currentPage = pageMatch[0];
        if (currentPage && currentPage?.params) {
          navigate(
            makeLink(currentPage.pattern.path, {
              ...currentPage.params,
              networkSlug: selectedNetworkSlug,
              controllerName,
            }),
          );
        }
      } else {
        const link = getNextBestCurrentPathForNetwork({
          companyName,
          networkSlug: selectedNetworkSlug,
          crumbs,
          currentPathname: location.pathname,
        });

        navigate(link);
      }
    },
    [
      companyName,
      location.pathname,
      navigate,
      networks,
      shouldNotChangePages,
      routePathsNotToChange,
      crumbs,
    ],
  );

  const sortedNetworks = useMemo(
    () =>
      networksToShow
        .map((network) => {
          const networkAddress = getNetworkAddress(network);

          const label = networkAddress ? `[${network.label}] ${networkAddress}` : network.label;
          return { label, network };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [networksToShow],
  );

  const groupedNetworks = useMemo(() => {
    const map = new Map<NetworkState, typeof sortedNetworks>();

    for (const obj of sortedNetworks) {
      const networkState = getNetworkState(obj.network);
      let arr = map.get(networkState);
      if (!arr) {
        arr = [];
        map.set(networkState, arr);
      }
      arr.push(obj);
    }

    return Array.from(map.entries()).sort(([a], [b]) => a - b);
  }, [sortedNetworks]);

  // Specifically looking at all networks here, not filtered
  if (networks.length <= 1) return null;
  return (
    <LocationSwitcherContainer view={view}>
      <ComboBox
        ref={ref}
        icon={breakpoint !== 'tablet' ? 'location' : undefined}
        onValueChange={valueSelected}
        value={networkSlug}
        size="large"
        aria-label="Switch location"
        canClearValue={false}
        placeholder="Select a location"
        width="100%"
        actions={
          (networksToShow.length < networks.length || showNonLiveNetworks) && (
            <LocationSwitcherActions>
              <LocationSwitcherInactiveToggle>
                <Label htmlFor="LocationSwitch-toggleActive">
                  {inOnboarding ? 'Show all networks' : 'Show inactive networks'}
                </Label>
                <ToggleInput
                  id="LocationSwitch-toggleActive"
                  selected={showNonLiveNetworks}
                  onChange={setShowNonLiveNetworks}
                  controlSize="small"
                />
              </LocationSwitcherInactiveToggle>
            </LocationSwitcherActions>
          )
        }
      >
        {groupedNetworks.length === 1
          ? groupedNetworks[0]?.[1]?.map(({ network, label }) => (
              <ComboBoxItem key={network.slug} textValue={label}>
                <LocationSwitcherOption network={network} label={label} />
              </ComboBoxItem>
            ))
          : groupedNetworks.map(([state, groupNetworks]) => (
              <ComboBoxSection title={networkStateLabel(state)}>
                {groupNetworks.map(({ network, label }) => (
                  <ComboBoxItem key={network.slug} textValue={label}>
                    <LocationSwitcherOption network={network} label={label} />
                  </ComboBoxItem>
                ))}
              </ComboBoxSection>
            ))}
      </ComboBox>
    </LocationSwitcherContainer>
  );
}
